"use client";

import { type FallbackMap, useSafeTranslations } from "@/lib/i18n/use-safe-translations";
import { useLocale } from "next-intl";
import { useEffect } from "react";
import { z } from "zod";

// MUST mirror i18n/messages/en.json#forms verbatim. Drift between
// this map and the catalog fires a server/client hydration mismatch (server
// renders the catalog value, client falls back here when the
// NextIntlClientProvider context is missing during a Turbopack HMR
// transient or an RSC retry-outside-provider). See
// `lib/i18n/use-safe-translations.ts` docstring + the
// feedback_safe_translations_drift auto-memory.
const FORMS_FALLBACKS: FallbackMap = {
  invalidEmail: "Not a valid email address.",
  invalidNumber: "Must be a number.",
  invalidUrl: "Not a valid URL.",
  required: "This field is required.",
  tooLong: "Too long.",
  tooShort: "Too short.",
};

export function ZodErrorMapBridge() {
  const locale = useLocale();
  const t = useSafeTranslations("forms", FORMS_FALLBACKS);
  useEffect(() => {
    z.setErrorMap((issue, ctx) => {
      switch (issue.code) {
        case z.ZodIssueCode.invalid_type:
          if (issue.received === "undefined" || issue.received === "null") {
            return { message: t("required") };
          }
          break;
        case z.ZodIssueCode.too_small:
          return { message: t("tooShort") };
        case z.ZodIssueCode.too_big:
          return { message: t("tooLong") };
        case z.ZodIssueCode.invalid_string:
          if (issue.validation === "email") return { message: t("invalidEmail") };
          if (issue.validation === "url") return { message: t("invalidUrl") };
          break;
        case z.ZodIssueCode.not_finite:
          return { message: t("invalidNumber") };
      }
      return { message: ctx.defaultError };
    });
  }, [locale, t]);
  return null;
}
